import { OBPlanIdValues } from '@buffer-mono/app-shell/src/components/PlanSelector/types'
import {
  Flex,
  Card,
  Text,
  Button,
  AchIcon,
  CreditCardIcon,
  AmazonPayIcon,
  AlipayIcon,
  CashappIcon,
  Notice,
  LinkStripeIcon,
  Separator,
  ExternalLinkIcon,
} from '@buffer-mono/popcorn'

import { addDays } from 'date-fns'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { capitalize } from '~publish/helpers/capitalize'
import {
  isCreditCardExpired,
  isCreditCardExpiring,
} from '~publish/helpers/creditCardExpiry'
import { useDismissObject } from '~publish/hooks/useDismissObject'
import { hasDismissedObject } from '~publish/legacy/utils/dismissedObjects/hasDismissedObject'

import { getSettingsUrl } from '../../shared/utils/getSettingsUrl'
import { SettingsRow } from '../../shared/components/SettingsRow'
import { useGetPortalSession } from '../hooks/useGetPortalSession'

import styles from './PaymentDetails.module.css'

import type { JSX } from 'react'

export const PaymentDetails_WebBillingDetails = graphql(/* GraphQL */ `
  fragment PaymentDetails_WebBillingDetails on WebBillingDetails {
    subscription {
      isPaymentPastDue
      plan {
        id
      }
      trial {
        startDate
        endDate
      }
    }
    paymentMethodDetails {
      __typename
      ... on CardPaymentMethodDetails {
        brand
        last4
        expMonth
        expYear
      }
      ... on BankPaymentMethodDetails {
        displayName
      }
      ... on LinkPaymentMethodDetails {
        displayName
      }
      ... on AmazonPayPaymentMethodDetails {
        displayName
      }
      ... on AlipayPaymentMethodDetails {
        displayName
      }
      ... on CashAppPaymentMethodDetails {
        displayName
      }
    }
  }
`)

type WebBilling = ResultOf<typeof PaymentDetails_WebBillingDetails>
type PaymentMethod = NonNullable<WebBilling['paymentMethodDetails']>

const PAST_DUE_NOTICE_DISMISSAL_EXPIRY_DAYS = 7

const paymentMethodIcons: Record<PaymentMethod['__typename'], JSX.Element> = {
  CardPaymentMethodDetails: <CreditCardIcon size="medium" />,
  BankPaymentMethodDetails: <AchIcon size="medium" />,
  LinkPaymentMethodDetails: <LinkStripeIcon size="medium" />,
  AmazonPayPaymentMethodDetails: <AmazonPayIcon size="medium" />,
  AlipayPaymentMethodDetails: <AlipayIcon size="medium" />,
  CashAppPaymentMethodDetails: <CashappIcon size="medium" />,
}

const CreditCardDetails = ({
  brand,
  last4,
  expMonth,
  expYear,
}: {
  brand: string
  last4: string
  expMonth: string
  expYear: string
}): JSX.Element => {
  return (
    <>
      <Flex gap="xs" direction="row" align="center">
        {paymentMethodIcons.CardPaymentMethodDetails}
        <Text>
          {capitalize(brand)}{' '}
          <span className={styles.creditCardLast4} aria-hidden>
            <span>••••</span> <span>••••</span> <span>••••</span>
          </span>{' '}
          {last4}
        </Text>
      </Flex>
      <Flex gap="xs" direction="row" align="center">
        <Text>
          Expires {expMonth}/{expYear}
        </Text>
      </Flex>
    </>
  )
}

const PaymentMethodDisplay = ({
  paymentMethod,
}: {
  paymentMethod: PaymentMethod
}): JSX.Element => {
  if (paymentMethod.__typename === 'CardPaymentMethodDetails') {
    return (
      <CreditCardDetails
        brand={paymentMethod.brand}
        last4={paymentMethod.last4}
        expMonth={paymentMethod.expMonth}
        expYear={paymentMethod.expYear}
      />
    )
  }
  return (
    <Flex gap="xs" direction="row" align="center">
      {paymentMethodIcons[paymentMethod.__typename]}
      <Text>{paymentMethod.displayName}</Text>
    </Flex>
  )
}

export const PaymentDetails = ({
  webBilling: webBillingRef,
  canAddPaymentDetails,
}: {
  webBilling: FragmentOf<typeof PaymentDetails_WebBillingDetails>
  canAddPaymentDetails: boolean
}): JSX.Element | null => {
  const { account } = useAccount()
  const billing = readFragment(PaymentDetails_WebBillingDetails, webBillingRef)
  const { dismissObject } = useDismissObject()

  const { getCustomerPortalSession, loading: portalSessionLoading } =
    useGetPortalSession()

  const { subscription, paymentMethodDetails } = billing
  const hasPaymentMethod = paymentMethodDetails != null

  const now = new Date()
  const isOnTrial =
    subscription.trial !== null &&
    new Date(subscription.trial.startDate) <= now &&
    now < new Date(subscription.trial.endDate)

  const isPaidPlan = subscription.plan.id !== OBPlanIdValues.free && !isOnTrial

  // Show component for edge case: user is on paid plan but has no payment details
  if (!hasPaymentMethod && !isPaidPlan) {
    return null
  }

  const paymentPastDue =
    Boolean(subscription.isPaymentPastDue) &&
    !hasDismissedObject({
      account,
      objectId: 'billing-notice-payment-past-due',
    })

  const card =
    paymentMethodDetails?.__typename === 'CardPaymentMethodDetails'
      ? paymentMethodDetails
      : null
  const isCardExpired = card !== null && isCreditCardExpired(card)
  const isCardExpiring =
    card !== null && !isCardExpired && isCreditCardExpiring(card)
  // The card identity in the id makes a dismissal specific to this card and
  // this state: a new card, or an expiring card becoming expired, produces a
  // fresh id and the notice reappears.
  const cardNoticeDismissalId = card
    ? `billing-notice-${isCardExpired ? 'expired-card' : 'expiring-card'}-${
        card.last4
      }-${card.expMonth}${card.expYear}`
    : null
  const showCardExpiryNotice =
    (isCardExpired || isCardExpiring) &&
    cardNoticeDismissalId !== null &&
    !hasDismissedObject({ account, objectId: cardNoticeDismissalId })

  return (
    <>
      <Separator />
      {paymentPastDue && (
        <Notice
          variant="warning"
          onDismiss={(): void => {
            dismissObject({
              id: 'billing-notice-payment-past-due',
              expiresAt: addDays(
                new Date(),
                PAST_DUE_NOTICE_DISMISSAL_EXPIRY_DAYS,
              ).toISOString(),
            })
          }}
        >
          <Text>
            Your payment is past due. Please update your payment method to
            continue using Buffer.
          </Text>
        </Notice>
      )}
      {showCardExpiryNotice && (
        <Notice
          variant={isCardExpired ? 'error' : 'warning'}
          onDismiss={(): void => {
            dismissObject({ id: cardNoticeDismissalId })
          }}
        >
          <Text>
            {isCardExpired
              ? 'Your card has expired. To keep your subscription running smoothly, update your billing details.'
              : `Your card's about to expire. To keep your subscription running smoothly, update your billing details.`}
          </Text>
        </Notice>
      )}
      <SettingsRow id="payment-method">
        <SettingsRow.Header>
          <SettingsRow.Title>Payment Method</SettingsRow.Title>
        </SettingsRow.Header>
        <SettingsRow.Control>
          <Button
            size="medium"
            variant="secondary"
            onClick={(): void => {
              getCustomerPortalSession({
                returnURL: getSettingsUrl(),
                cta: 'billing-paymentDetails-updateMethod-1',
              })
            }}
            disabled={portalSessionLoading || !canAddPaymentDetails}
          >
            Update Method
            <ExternalLinkIcon />
          </Button>
        </SettingsRow.Control>
      </SettingsRow>
      <Card className={styles.card}>
        <Flex
          gap="xs"
          direction="row"
          align="center"
          justify="between"
          fullWidth
        >
          {paymentMethodDetails ? (
            <PaymentMethodDisplay paymentMethod={paymentMethodDetails} />
          ) : (
            <Flex gap="xs" direction="row" align="center">
              <Text color="subtle">No payment method on file</Text>
            </Flex>
          )}
        </Flex>
      </Card>
    </>
  )
}

export default PaymentDetails
